import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FundOutlined, AppstoreOutlined, DeploymentUnitOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Menu, Tooltip, theme } from 'antd';

export type ModuleKey = 'monitor' | 'studio' | 'devices';

const MODULE_ROUTES: Record<ModuleKey, string> = {
  monitor: '/',
  studio: '/studio',
  devices: '/devices',
};

/** Derive the active module key from a pathname. Monitor is the fallback. */
export function getActiveModuleKey(pathname: string): ModuleKey {
  if (pathname === '/studio' || pathname.startsWith('/studio/')) return 'studio';
  if (pathname === '/devices' || pathname.startsWith('/devices/')) return 'devices';
  return 'monitor';
}

/**
 * Strip module-specific URL params when LEAVING the module they belong to:
 *
 * - Studio's `roomId` / `zoneId` mean "scroll to this item" inside the
 *   canvas and can go stale (canvas-deselect doesn't update the URL).
 *   Going INTO Studio keeps them so the canvas can focus on the user's
 *   last-viewed room.
 * - Devices' `siteId` is a Site-level filter that only Devices reads;
 *   leaving it on the URL after switching to Monitor / Studio would
 *   short-circuit those modules' default-space redirect and land the
 *   user on an empty dashboard.
 *
 * Returns the search string with leading `?`, or `''` when no params
 * remain. Module-level scope params not specific to source/destination
 * (`spaceId`, `buildingId`) flow through unchanged.
 */
export function buildModuleSearch(
  active: ModuleKey,
  destination: ModuleKey,
  search: string,
): string {
  const params = new URLSearchParams(search);
  if (active === 'studio' && destination !== 'studio') {
    params.delete('roomId');
    params.delete('zoneId');
  }
  if (active === 'devices' && destination !== 'devices') {
    params.delete('siteId');
  }
  const out = params.toString();
  return out ? `?${out}` : '';
}

const items = [
  { key: 'monitor' as const, icon: <FundOutlined />, label: 'Monitor' },
  { key: 'studio' as const, icon: <AppstoreOutlined />, label: 'Studio' },
  { key: 'devices' as const, icon: <DeploymentUnitOutlined />, label: 'Devices' },
];

/**
 * Top section of the sidebar: switches between the three top-level
 * product modules (Monitor, Studio, Devices). In the expanded sidebar
 * this is an antd `Menu` with pill-shaped items; in the collapsed
 * sidebar it's a vertical stack of circular icon buttons so it visually
 * matches the circular fold/unfold button above it.
 */
export function ModuleSwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { token } = theme.useToken();
  const active = getActiveModuleKey(pathname);

  // Memoize the ConfigProvider theme so antd doesn't re-derive Menu tokens
  // on every render. Only `token.colorError` is dynamic; the colour values
  // themselves are intentionally hard-coded design-system reds.
  const menuTheme = useMemo(
    () => ({
      components: {
        Menu: {
          // Red accent for module selection and hover (distinct from the
          // neutral state used by the Spaces menu below). Routes through
          // `--color-module-active-bg` so the pill background flips to a
          // darker, dark-mode-friendly tint when `html.dark` is set.
          itemSelectedBg: 'var(--color-module-active-bg)',
          itemSelectedColor: token.colorError,
          itemHoverBg: 'var(--color-module-active-bg)',
          itemHoverColor: token.colorError,
          // Pill-shaped items
          itemBorderRadius: 9999,
          itemHeight: 36,
        },
      },
    }),
    [token.colorError],
  );

  const moduleSearch = (destination: ModuleKey) => buildModuleSearch(active, destination, search);

  if (collapsed) {
    // `py-1` matches antd Menu's default `itemMarginBlock: 4` in the expanded
    // state so the first icon lands at the same Y as Monitor in the full Menu
    // — no visible jump when toggling the sidebar. `w-full` makes the flex
    // container span the collapsed sidebar column so `items-center` actually
    // centres the 36px buttons against the full 44px width.
    return (
      <div className="flex w-full flex-col items-center gap-1 py-1">
        {items.map((item) => {
          const isActive = item.key === active;
          return (
            <Tooltip key={item.key} title={item.label} placement="right">
              <Button
                shape="circle"
                type="text"
                icon={item.icon}
                onClick={() => navigate(MODULE_ROUTES[item.key] + moduleSearch(item.key))}
                aria-label={item.label}
                style={{
                  width: 36,
                  height: 36,
                  ...(isActive && {
                    backgroundColor: 'var(--color-module-active-bg)',
                    color: token.colorError,
                  }),
                }}
              />
            </Tooltip>
          );
        })}
      </div>
    );
  }

  return (
    <ConfigProvider theme={menuTheme}>
      <Menu
        mode="inline"
        inlineIndent={20}
        selectedKeys={[active]}
        items={items}
        onClick={({ key }) =>
          navigate(MODULE_ROUTES[key as ModuleKey] + moduleSearch(key as ModuleKey))
        }
        style={{ background: 'transparent', border: 'none' }}
      />
    </ConfigProvider>
  );
}
